import { Space } from "antd";
import Table, { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { useAuth } from "src/core/hooks/useAuth";
import { routingsService } from "src/core/services/costExplosionServices/routingsService";
import { useRoutingsDataItemColumns } from "src/modules/CostExplosionManager/tabs/RoutingsTab/components/RoutingsDetailTable/hooks/useRoutingsDataItemColumns";
import { useRoutingsDataItemData } from "src/modules/CostExplosionManager/tabs/RoutingsTab/components/RoutingsDetailTable/hooks/useRoutingsDataItemData";
import { TableColumnsSelector } from "src/modules/TableColumnsSelector/TableColumnsSelector";
import { appParamsState } from "src/recoil/atoms";
import {
  costExplosionAppState,
  costExplosionGlobalState,
} from "src/recoil/costExplosionAtoms";
import { ThresholdData } from "src/types/common";
import { RoutingsDataItem } from "src/types/costExplosion";

export const RoutingsDetailTable: React.FC = () => {
  const [tableLoading, setTableLoading] = useState(false);

  const { accessToken, success, authLoaded } = useAuth();

  const [dataItems, setDataItems] = useState<RoutingsDataItem[]>([]);
  const [thresholdItems, setThresholdItems] = useState<ThresholdData[]>([]);

  const appParams = useRecoilValue(appParamsState);

  const costExplosionAppStateData = useRecoilValue(costExplosionAppState);

  const costExplosionState = useRecoilValue(costExplosionGlobalState);

  const { processed, processedData } = useRoutingsDataItemData(dataItems);
  const { getTableColumns } = useRoutingsDataItemColumns(
    processedData,
    thresholdItems
  );

  const [tableColumns, setTableColumns] = useState<
    ColumnsType<RoutingsDataItem>
  >([]);
  const [filteredTableColumns, setFilteredTableColumns] = useState<
    ColumnsType<RoutingsDataItem>
  >([]);

  useEffect(() => {
    (async () => {
      setTableColumns(getTableColumns());
      setFilteredTableColumns(getTableColumns());
    })();
  }, [getTableColumns]);

  useEffect(() => {
    (async () => {
      if (authLoaded && success) {
        if (!costExplosionAppStateData.selectingModelSku) {
          if (
            costExplosionState.selectedModelSkus &&
            costExplosionState.selectedModelSkus?.length > 0
          ) {
            setTableLoading(true);

            let skusList: string[] = [];

            costExplosionState.selectedModelSkus.forEach((m) => {
              if (m.sku) {
                skusList.push(m.sku);
              }
            });

            if (skusList.length > 0) {
              const response =
                await routingsService.fetchRoutingsDetailDataItems(
                  accessToken,
                  costExplosionState.modelNumber ?? "",
                  skusList,
                  costExplosionState.adjustmentsEnabled
                );

              setDataItems(response.data);
              setThresholdItems(response.thresholds);
            }

            setTableLoading(false);
          }
        }
      }
    })();
  }, [
    accessToken,
    authLoaded,
    costExplosionAppStateData.selectingModelSku,
    costExplosionState.adjustmentsEnabled,
    costExplosionState.modelNumber,
    costExplosionState.selectedModelSkus,
    success,
  ]);

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      <TableColumnsSelector
        baseColumns={tableColumns as []}
        setTableColumns={(columnsList) => {
          setFilteredTableColumns(columnsList as ColumnsType<RoutingsDataItem>);
        }}
      />
      <Table
        columns={filteredTableColumns}
        loading={tableLoading || !processed}
        dataSource={processedData}
        pagination={false}
        rowKey={({ rowKey }) => `${rowKey}`}
        className="stickied-table"
        sticky={true}
        scroll={{ x: "max-content", y: 500 }}
        style={{ width: appParams.contentWidth - 60 }}
        rowClassName={(row) =>
          (row.rowType ?? null) !== null ? `row-type-class-${row.rowType}` : ""
        }
        size={"small"}
        bordered
      />
    </Space>
  );
};
